/**
 * JSON-LD builders, one per page type, following the specs in /SEO.
 *
 * Two rules govern everything here:
 *
 * 1. Structured data must describe what is actually on the page. An
 *    aggregateRating is only emitted where a rating is really shown, an FAQPage
 *    only where the questions are really rendered. Mismatched markup is a
 *    manual-action risk, not just a wasted opportunity.
 * 2. No placeholders. The /SEO specs carry sample values
 *    ("YOUR-FACEBOOK-URL", example addresses, a sample aggregateRating). Fields
 *    we hold no real data for are omitted rather than guessed.
 */

export const SITE = "https://myvaidyam.com";
export const ORG_ID = `${SITE}/#org`;
export const WEBSITE_ID = `${SITE}/#website`;

/**
 * Date the site's content was last revised, as YYYY-MM-DD, used for
 * `dateModified`. Maintained by hand on purpose: deriving it from the build
 * would push a new date onto every page on every deploy, including pages whose
 * content did not change. Bump it when content actually changes.
 */
export const CONTENT_UPDATED = "2026-09-18";

type Json = Record<string, unknown>;

/** Drops undefined/empty values so no half-filled property is published. */
const clean = (o: Json): Json =>
  Object.fromEntries(
    Object.entries(o).filter(([, v]) => {
      if (v === undefined || v === null || v === "") return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
  );

export const abs = (pathOrUrl: string) =>
  /^https?:\/\//.test(pathOrUrl) ? pathOrUrl : SITE + encodeURI(pathOrUrl);

/* ------------------------------ Organization ----------------------------- */
/**
 * Served on every page, as its own block beside WebSite (see webSiteNode for
 * why they are not one @graph), per SEO/Organization.txt.
 *
 * `alternateName` gives the other ways people write the name, so a search for
 * "my vaidyam" is matched to the brand; `sameAs` lists MyVaidyam's own social
 * profiles, the footer's links (components/Footer SOCIAL): keep them in step.
 */
export const BRAND_ALTERNATE_NAMES = ["My Vaidyam", "MyVaidyam.com", "My Vaidyam India"];
const SAME_AS = [
  "https://www.facebook.com/people/MyVaidyam-Wellnesss-India/61595155291248/",
  "https://x.com/myvaidyam",
  "https://www.pinterest.com/myvaidyamindia/",
  "https://www.instagram.com/myvaidyam/",
  "https://www.threads.com/@myvaidyam",
  "https://www.youtube.com/@MyVaidyam",
];

export function organizationNode() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
        name: "MyVaidyam",
        alternateName: BRAND_ALTERNATE_NAMES,
        legalName: "MyVaidyam Ayurveda & Holistic Wellness",
        slogan: "Where Ayurveda Meets Medical Trust",
        description:
          "MyVaidyam helps international patients discover authentic Ayurveda treatments, wellness programs and trusted Ayurveda centres across India with expert guidance.",
        url: `${SITE}/`,
        logo: {
          "@type": "ImageObject",
          "@id": `${SITE}/#logo`,
          url: `${SITE}/myvaidyam-logo.png`,
          contentUrl: `${SITE}/myvaidyam-logo.png`,
        },
        image: `${SITE}/myvaidyam-logo.png`,
        sameAs: SAME_AS,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Sea Breeze Towers, Tower-5, F-1203, Palm Beach Road",
          addressLocality: "Nerul, Navi Mumbai",
          addressRegion: "Maharashtra",
          postalCode: "400705",
          addressCountry: "IN",
        },
        contactPoint: [
          {
            "@type": "ContactPoint",
            contactType: "customer support",
            telephone: "+91-9156115572",
            email: "care@myvaidyam.com",
            availableLanguage: ["en", "ar", "fr", "ru", "de", "es", "it", "nl", "sv", "da"],
            areaServed: [
              "DE", "FR", "ES", "GB", "IT", "US", "RU", "NL", "IE", "CH", "SK", "IL", "BG",
              "UA", "PL", "AU", "LU", "NO", "DK", "AT", "SE", "RO", "HU", "PT", "GR", "CA",
              "NZ", "SA", "AE",
            ],
          },
        ],
        knowsAbout: [
          "Ayurvedic Treatment in India",
          "Panchakarma Treatment",
          "Ayurvedic Medicine",
          "Ayurvedic Therapies",
          "Ayurvedic Hospitals",
          "Ayurvedic Treatment Centers",
          "Ayurvedic Wellness Retreats",
          "Ayurveda Therapy in India",
          "Ayurvedic Rehabilitation",
          "Ayurvedic Wellness Programs",
      "Ayurveda Tourism in India",
    ],
  };
}

/**
 * WebSite, emitted as its own block.
 *
 * Organization and WebSite are deliberately NOT combined in one @graph, and
 * WebSite deliberately carries no `publisher` reference to the Organization.
 * Both were tried: validators resolve an @id reference and nest the target
 * inside the item that points at it, so the Organization stopped being listed
 * as a detected item and the home page reported WebSite alone. With nothing
 * referencing it, each type is reported separately — which is the point of
 * splitting them. They remain linked for consumers that care, because both
 * carry a stable @id on the same domain.
 */
export function webSiteNode() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: `${SITE}/`,
    // the site name Google shows in results, and the other spellings of it
    name: "MyVaidyam",
    alternateName: BRAND_ALTERNATE_NAMES,
  };
}

/* ------------------------------ BreadcrumbList --------------------------- */
export function breadcrumb(trail: { name: string; path?: string }[], id?: string) {
  return clean({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": id,
    itemListElement: trail.map((c, i) =>
      clean({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: c.path ? abs(c.path) : undefined,
      })
    ),
  });
}

/* --------------------------------- FAQPage -------------------------------- */
/** Only call this when the questions are actually rendered on the page. */
export function faqPage(faqs: { question: string; answer: string }[]) {
  if (!faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/* ------------------------- no links between blocks ----------------------- */
/**
 * Every block below is self-contained: none carries an `@id` reference to
 * another block on the page. Organization and WebSite are served on every page,
 * and the validators this site is checked with fold any referenced node into the
 * item that references it — a `publisher: {"@id": "#org"}` makes Organization
 * disappear from the detected list, a `mainEntity: {"@id": "#therapy"}` does the
 * same to MedicalTherapy. The requirement is that each type is reported as its
 * own item, so details that used to be references (the publisher) are written
 * out inline without an @id, and pure pointers (isPartOf, about, mainEntity,
 * breadcrumb, mainEntityOfPage, parentOrganization) are left out.
 */

/* ------------------------------ MedicalClinic ----------------------------- */
/**
 * Per SEO/MedicalClinic.txt. Telephone, geo coordinates, priceRange and payment
 * details are omitted — the site holds none of those per centre, and the spec's
 * values are worked examples. addressLocality carries the centre's real
 * location string; aggregateRating is only included when a rating is displayed.
 */
export function medicalClinic(c: {
  name: string;
  path: string;
  description?: string;
  images?: string[];
  location?: string;
  /** Full postal address, only where the page states a real one. */
  streetAddress?: string;
  addressRegion?: string;
  postalCode?: string;
  rating?: number;
  reviews?: number;
}) {
  const url = abs(c.path);
  return clean({
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "@id": `${url}#clinic`,
    name: c.name,
    url,
    image: (c.images ?? []).filter(Boolean).map(abs),
    description: c.description,
    // No `medicalSpecialty`: same enumeration as MedicalWebPage.specialty, with
    // no Ayurveda value — free text there fails validation (see medicalWebPage).
    address: clean({
      "@type": "PostalAddress",
      streetAddress: c.streetAddress,
      addressLocality: c.location,
      addressRegion: c.addressRegion,
      postalCode: c.postalCode,
      addressCountry: "IN",
    }),
    aggregateRating:
      typeof c.rating === "number" && typeof c.reviews === "number" && c.reviews > 0
        ? {
            "@type": "AggregateRating",
            ratingValue: String(c.rating),
            reviewCount: String(c.reviews),
          }
        : undefined,
  });
}

/* ----------------------- MedicalTherapy + MedicalWebPage ------------------ */
/** Per SEO/MedicalTherapy.txt and SEO/MedicalWebPage.txt — used together. */
export function medicalTherapy(t: {
  name: string;
  path: string;
  description?: string;
  alternateName?: string[];
}) {
  const url = abs(t.path);
  return clean({
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "@id": `${url}#therapy`,
    name: t.name,
    alternateName: t.alternateName,
    description: t.description,
    url,
    medicineSystem: "Ayurvedic",
  });
}

/**
 * What a voice assistant may read aloud on any page: its title and its meta
 * description, the pair Google's own speakable example uses. Both are in the
 * head the prerender bakes into every route, so they are there whether or not
 * the reader runs JavaScript; the page body (its h1) is not — the app draws
 * it in the browser — and validators reject combinators such as "h1 ~ p".
 */
export const SPEAKABLE = {
  "@type": "SpeakableSpecification",
  xpath: ["/html/head/title", "/html/head/meta[@name='description']/@content"],
};

export function medicalWebPage(p: {
  name: string;
  path: string;
  description?: string;
  /**
   * When this page's content was first published, as YYYY-MM-DD. Left unset by
   * default: the site keeps no publication record per page, and a made-up date
   * is a factual claim search engines act on. Pass it where it is known.
   */
  datePublished?: string;
  /** Defaults to CONTENT_UPDATED. Pass a page-specific date where one is known. */
  dateModified?: string;
}) {
  const url = abs(p.path);
  return clean({
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "@id": `${url}#webpage`,
    url,
    name: p.name,
    description: p.description,
    // Written out, with no @id: an @id here would make validators fold the
    // page's own Organization block into this item. No logo here: the page's
    // picture is its own photo (image / primaryImageOfPage, added from its
    // sharing image in hooks/useDocumentHead), and the logo stays on the
    // Organization block (client, 2026-11-02).
    publisher: {
      "@type": "MedicalOrganization",
      name: "MyVaidyam",
      url: `${SITE}/`,
    },
    audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    // No `specialty`: it only accepts schema.org's MedicalSpecialty values
    // (Dermatology, Physiotherapy, …), none of which is Ayurveda, and a free-text
    // value fails validation. Choosing the nearest enum would misdescribe the page.
    datePublished: p.datePublished,
    dateModified: p.dateModified ?? CONTENT_UPDATED,
    inLanguage: "en-IN",
    speakable: SPEAKABLE,
  });
}

/* ------------------------------- VideoObject ------------------------------ */
/**
 * Per SEO/Video Schema.txt. uploadDate and duration are required by Google for
 * video rich results but we hold neither, so they are omitted rather than
 * invented — a wrong uploadDate is worse than an absent one.
 */
export function videoObject(v: {
  name: string;
  description: string;
  youtubeId: string;
  uploadDate?: string;
  duration?: string;
  /**
   * The channel that actually published the video. These are the centres' own
   * YouTube channels, NOT MyVaidyam — naming MyVaidyam as publisher would be a
   * false claim of authorship, so when the channel is unknown the property is
   * omitted rather than defaulted to the site's own organisation.
   */
  publisherName?: string;
}) {
  return clean({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: v.name,
    description: v.description,
    thumbnailUrl: `https://i.ytimg.com/vi/${v.youtubeId}/maxresdefault.jpg`,
    embedUrl: `https://www.youtube.com/embed/${v.youtubeId}`,
    contentUrl: `https://www.youtube.com/watch?v=${v.youtubeId}`,
    uploadDate: v.uploadDate,
    duration: v.duration,
    inLanguage: "en",
    publisher: v.publisherName
      ? { "@type": "Organization", name: v.publisherName }
      : undefined,
  });
}
